import { Uuid } from "@/domains/global/identifiers";
import { UpdateAssistantTaskOperation } from "@/store/sync/operations/assistant-tasks/UpdateAssistantTaskOperation";
import type { AssistantTaskModelData, AssistantTaskStatus } from "@/store/assistant/types";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import type { IUpdateAssistantTaskOperation } from "@/store/sync/operations/types";
import { SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { WithAppStore } from "@/store/types";
import { computed, makeObservable, observable } from "mobx";

export class AssistantTaskObservable extends BaseSyncModel<AssistantTaskModelData> {
  public modelKind = SyncModelKind.AssistantTask;

  constructor({
    id,
    data,
    store,
  }: { id: Uuid; data: SyncUpdateValue<AssistantTaskModelData> } & WithAppStore) {
    super({ id, data, store });
    makeObservable(this, {
      modelKind: observable,
      assistantId: computed,
      projectId: computed,
      title: computed,
      description: computed,
      status: computed,
      dueAt: computed,
      createdAt: computed,
      updatedAt: computed,
      update: false,
      updateStatus: false,
    });
  }

  get assistantId(): Uuid {
    return this.modelData.assistant_id;
  }

  get projectId(): Uuid | null {
    return this.modelData.project_id;
  }

  get title(): string {
    return this.modelData.title;
  }

  get description(): string | null {
    return this.modelData.description;
  }

  get status(): string {
    return this.modelData.status;
  }

  get dueAt(): string | null {
    return this.modelData.due_at;
  }

  get createdAt(): string {
    return this.modelData.created_at;
  }

  get updatedAt(): string {
    return this.modelData.updated_at;
  }

  async updateStatus({ status }: { status: AssistantTaskStatus }): Promise<void> {
    await this.update({ status });
  }

  async update({
    title,
    description,
    status,
    dueAt,
    projectId,
  }: {
    title?: string;
    description?: string | null;
    status?: AssistantTaskStatus;
    dueAt?: string | null;
    projectId?: Uuid | null;
  }): Promise<void> {
    const payload: Omit<IUpdateAssistantTaskOperation["payload"], "schema_version"> = {
      id: this.id,
    };

    if (title !== undefined) {
      payload.title = title;
    }

    if (description !== undefined) {
      payload.description = description;
    }

    if (status !== undefined) {
      payload.status = status;
    }

    if (dueAt === null) {
      payload.clear_due_at = true;
    } else if (dueAt !== undefined) {
      payload.due_at = dueAt;
    }

    if (projectId === null) {
      payload.clear_project_id = true;
    } else if (projectId !== undefined) {
      payload.project_id = projectId;
    }

    await new UpdateAssistantTaskOperation({
      store: this.store,
      payload,
    }).execute();
  }
}
